import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { EditServiceAreaForm } from "./edit-service-area-form";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useServiceAreas } from "@/api/service-areas";
import { ServiceArea } from "@/api.types";
import { CreateServiceAreaButton } from "./create-service-area-button";

export function ServiceAreasTable() {
  const [editingArea, setEditingArea] = useState<ServiceArea | null>(null);

  const { data: serviceAreas, isLoading } = useServiceAreas();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="flex justify-end mb-4 gap-4">
        <CreateServiceAreaButton />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service Area</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Capacity</TableHead>
              <TableHead>Coverage</TableHead>
              <TableHead>Operating Hours</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {serviceAreas?.map((area) => (
              <TableRow key={area.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{area.name}</p>
                    <p className="text-sm text-gray-500">Code: {area.code}</p>
                    <p className="text-sm text-gray-500">
                      Depot: {area.depot.name}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="secondary"
                    className={`${
                      area.isActive ? "bg-green-500" : "bg-gray-500"
                    } text-white`}
                  >
                    {area.isActive ? "ACTIVE" : "INACTIVE"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    {area.maxDailyOrders && (
                      <p className="text-sm">
                        Max Daily Orders: {area.maxDailyOrders}
                      </p>
                    )}
                    {area.maxVehicles && (
                      <p className="text-sm">
                        Max Vehicles: {area.maxVehicles}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">
                      {area.postcodes.length} postcodes covered
                    </p>
                    <p className="text-sm text-gray-500">
                      {area.boundary.coordinates[0].length} boundary points
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    {area.operatingWindows.map((window) => (
                      <div key={window.id} className="mb-1">
                        <p className="text-sm">
                          {window.startTime} - {window.endTime}
                        </p>
                        <p className="text-xs text-gray-500">
                          Days:{" "}
                          {window.daysOfWeek
                            .map(
                              (day) =>
                                [
                                  "Sun",
                                  "Mon",
                                  "Tue",
                                  "Wed",
                                  "Thu",
                                  "Fri",
                                  "Sat",
                                ][day]
                            )
                            .join(", ")}
                        </p>
                      </div>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingArea(area)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editingArea} onOpenChange={() => setEditingArea(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Service Area</DialogTitle>
          </DialogHeader>
          {editingArea && (
            <EditServiceAreaForm
              serviceArea={editingArea}
              onClose={() => setEditingArea(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
